import _ from 'lodash';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import gsap from 'gsap';
import classnames from 'classnames';
import Link from 'next/link';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

import { useWindowSize } from 'react-use';
import useRWD from 'hooks/useRWD';

import WebpImg from 'components/WebpImg';
import useSWR from 'swr';
import api, { swrFetcher } from 'utils/api';

import MaxWidthContainer from 'components/MaxWidthContainer';
import AnimateTargetNumber from 'components/AnimateTargetNumber';
import { SorraCircle, SorraStar } from 'components/2025/SorraDecos';

import { results } from 'utils/voting2025';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const { useState, useEffect, useRef, forwardRef } = React;

const CardTitle = ({ n = 0, title = '' }) => {
  return (
    <div className="absolute left-0 right-0 top-0 flex -translate-y-1/2 items-center justify-center gap-x-2">
      <div className="flex items-center gap-x-px font-rubik font-normal text-sorra-pink lg:gap-x-2">
        <p className="text-xl lg:text-[32px]">(</p>
        <p className="w-7 text-center text-xl font-medium lg:text-2xl">Q{n}.</p>
        <p className="text-xl lg:text-[32px]">)</p>
      </div>
      <p className="font-jinxuanlatte text-xl font-medium text-black lg:text-2xl">
        {title}
      </p>
    </div>
  );
};

const SpinningNumber = (props) => {
  const {
    number,
    width = 20,
    fontSize = 28,
    duration = 0.7,
    animate = true,
  } = props;
  const digits = number
    .toString()
    .split('')
    .map((digit) => parseInt(digit, 10));
  const height = fontSize * 1.25 + 8;
  return (
    <>
      {digits.map((digit, index) => {
        return (
          <React.Fragment key={index}>
            <AnimateTargetNumber
              target={digit}
              repeat={index + 1}
              animate={animate}
              duration={duration}
              width={width}
              fontSize={fontSize}
              className="!font-jinxuanlatte !font-bold"
            />
            {(digits.length - (index + 1)) % 3 == 0 &&
              index != digits.length - 1 && (
                <div
                  style={{
                    height: `${height}px`,
                    fontSize: `${fontSize}px`,
                  }}
                  className="relative flex items-center"
                >
                  <p className="text-end font-jinxuanlatte font-bold text-sorra-pink">
                    ,
                  </p>
                </div>
              )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const RollingItem = (props) => {
  const { i } = props;
  const result = results[i];
  const firstProductId = _.first(result.productIds);

  const { data: product } = useSWR(
    `/api/beauty-award/product/${firstProductId}`,
    swrFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return (
    <div
      style={{
        boxShadow: '3px 3px 9.6px 0 rgba(0, 0, 0, 0.10)',
      }}
      className="relative flex aspect-[214/268] h-full flex-col items-center rounded-[10px] bg-white px-3 pt-2 lg:px-6 lg:pt-4"
    >
      <div className="absolute bottom-5 left-0 aspect-[32/31] w-[30%] -translate-x-[75%] lg:bottom-9">
        <img
          className="h-full w-full object-contain"
          src="/images/beauty-award/2025/bow_wing.png"
        />
      </div>
      <div className="absolute bottom-5 right-0 aspect-[32/31] w-[30%] translate-x-[75%] scale-x-[-100%] lg:bottom-9">
        <img
          className="h-full w-full object-contain"
          src="/images/beauty-award/2025/bow_wing.png"
        />
      </div>
      <div className="absolute inset-0 rounded-[10px] bg-white"></div>

      <img
        className="relative aspect-[20/14] w-[10px] object-contain lg:w-[20px]"
        src={`/images/beauty-award/2025/crown.png`}
      />
      <div className="relative mt-px flex flex-row items-center gap-x-1 font-rubik text-[8px] font-semibold text-sorra-pink lg:mt-1.5 lg:text-sm">
        <p>第</p>
        <span className="text-[15px] lg:text-[32px]">1</span>
        <p>位</p>
      </div>
      <div className="relative mt-0 w-full lg:mt-[14px]">
        <WebpImg
          className="aspect-[161/159] w-full object-contain"
          src={
            product && !_.isEmpty(product.images)
              ? product.images[0].url
              : '/images/placeholders/product.png'
          }
        />
      </div>
      <div className="absolute bottom-2 aspect-[215/51] w-[112%] lg:bottom-4">
        <img
          className="h-full w-full object-contain"
          src="/images/beauty-award/2025/bow_body.png"
        />
        <div className="absolute bottom-[10%] left-0 right-0 flex w-full justify-center lg:bottom-[15%]">
          <p className="text-[10px] font-bold text-white lg:text-[20px]">
            {result.name}
          </p>
        </div>
      </div>
      {/* <div className="absolute"></div> */}
    </div>
  );
};

const BeautyAwards2025Result = (props) => {
  const router = useRouter();
  const { isLg } = useRWD();

  const resultKeys = _.keys(results);
  const resultKeyLength = resultKeys.length;

  // console.log({ resultKeyLength });

  // const { pathname, asPath, isReady } = router;

  // const { user, getAuthorizationHeader, isSignInStatusReady } = useUserAuth();

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [desktopScale, setDesktopScale] = useState(1);
  const desktopBaseWidth = 1440;
  const desktopBaseHeight = 705;

  const expired = false;
  const isLoginPage = false;

  // const [animateTrigger, setAnimateTrigger] = useState(false);
  const [originStrategy, setOriginStrategy] = useState('center center');

  const planeRef = useRef(null);
  const redBowRef = useRef(null);
  const blueBowRef = useRef(null);
  const lipRef = useRef(null);
  const ballonRef = useRef(null);
  const desktopFrameRef = useRef(null);

  const mobilePlaneRef = useRef(null);
  const mobileBallonRef = useRef(null);
  const mobileRabbitRef = useRef(null);

  const [featureFirst, setFeatureFirst] = useState(0);

  const [animateTrigger, setAnimateTrigger] = useState(false);
  const [disableVoting, setDisableVoting] = useState(false);

  useEffect(() => {
    let interval = null;
    interval = setInterval(() => {
      setFeatureFirst((prev) => {
        return (prev + 1) % resultKeyLength;
      });
    }, 3000);
    return () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
  }, []);

  useEffect(() => {
    gsap.to(
      `.feature-${(featureFirst + (resultKeyLength - 3)) % resultKeyLength}`,
      {
        x: '-300%',
        opacity: 0,
        scale: 0.7,
        onComplete: () => {
          gsap.set(
            `.feature-${(featureFirst + (resultKeyLength - 3)) % resultKeyLength}`,
            {
              x: '300%',
              y: '0px',
              scale: 0.7,
              opacity: 0,
              filter: 'blur(0.5px)',
            }
          );
        },
      }
    );
    gsap.to(
      `.feature-${(featureFirst + (resultKeyLength - 2)) % resultKeyLength}`,
      {
        x: '-225%',
        y: '0px',
        scale: 0.8,
        opacity: 1,
        filter: 'blur(2px)',
      }
    );
    gsap.to(
      `.feature-${(featureFirst + (resultKeyLength - 1)) % resultKeyLength}`,
      {
        x: '-115%',
        y: '0px',
        scale: 0.9,
        opacity: 1,
        filter: 'blur(1px)',
      }
    );
    gsap.to(`.feature-${featureFirst}`, {
      x: '0%',
      y: isLg ? '0px' : '10px',
      scale: 1,
      opacity: 1,
      filter: 'blur(0px)',
    });
    gsap.to(`.feature-${(featureFirst + 1) % resultKeyLength}`, {
      x: '115%',
      y: '0px',
      scale: 0.9,
      opacity: 1,
      filter: 'blur(1px)',
    });
    gsap.to(`.feature-${(featureFirst + 2) % resultKeyLength}`, {
      x: '225%',
      y: '0px',
      scale: 0.8,
      opacity: 1,
      filter: 'blur(2px)',
    });
  }, [featureFirst, isLg]);

  useGSAP(() => {
    gsap.fromTo(
      planeRef.current,
      {
        opacity: 0,
        x: 50,
        y: -50,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 0.8,
        ease: 'power2.in',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(planeRef.current, {
            y: '+=15',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      ballonRef.current,
      {
        opacity: 0,
        x: -100,
        y: -100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(ballonRef.current, {
            y: '+=10',
            duration: 3,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      lipRef.current,
      {
        opacity: 0,
        x: -100,
        y: +100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(lipRef.current, {
            y: '+=10',
            duration: 3,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      redBowRef.current,
      {
        opacity: 0,
        x: -100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(redBowRef.current, {
            y: '+=5',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      redBowRef.current,
      {
        opacity: 0,
        x: -100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(redBowRef.current, {
            y: '+=5',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      blueBowRef.current,
      {
        opacity: 0,
        x: 100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          gsap.to(blueBowRef.current, {
            y: '+=8',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      desktopFrameRef.current,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 2,
        ease: 'power2.out',
      }
    );

    gsap.fromTo(
      mobilePlaneRef.current,
      {
        opacity: 0,
        x: '-100%',
        y: '-100%',
        scale: 2,
        rotate: -3.019,
      },
      {
        opacity: 1,
        scale: 1,
        x: '0%',
        y: '-50%',
        duration: 1.2,
        rotate: -3.019,
        ease: 'power2.in',
        onComplete: () => {
          gsap.to(mobilePlaneRef.current, {
            y: '+=8',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );

    gsap.fromTo(
      mobileBallonRef.current,
      {
        opacity: 0,
        x: 40,
        y: 40,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          gsap.to(mobileBallonRef.current, {
            y: '+=8',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );

    return () => {
      gsap.killTweensOf(planeRef.current);
      gsap.killTweensOf(ballonRef.current);
      gsap.killTweensOf(redBowRef.current);
      gsap.killTweensOf(blueBowRef.current);
      gsap.killTweensOf(desktopFrameRef.current);

      gsap.killTweensOf(mobilePlaneRef.current);
      gsap.killTweensOf(mobileBallonRef.current);
      // gsap.killTweensOf('.mobileCard');
    };
  }, []);

  useEffect(() => {
    if (windowWidth && windowHeight) {
      const baseProportion = 705 / 1440;
      const currentProportion = windowHeight / windowWidth;
      if (currentProportion >= baseProportion) {
        setDesktopScale(windowWidth / desktopBaseWidth);
        setOriginStrategy('top center');
      } else {
        setDesktopScale(windowHeight / desktopBaseHeight);
        setOriginStrategy('top center');
      }
    }
  }, [windowWidth, windowHeight]);

  // const { data: myVotingAnswer } = useSWR(
  //   user ? '/api/beauty-award/me/votingAnswer' : null,
  //   (url) => swrFetcher(url, getAuthorizationHeader),
  //   {
  //     revalidateOnFocus: false,
  //   }
  // );

  // useEffect(() => {
  //   if (isSignInStatusReady && myVotingAnswer) {
  //     setDisableVoting(true);
  //   }
  // }, [isSignInStatusReady, myVotingAnswer]);

  useGSAP(() => {
    ScrollTrigger.create({
      scroller: '#app-wrapper',
      start: 'top 80%',
      end: 'bottom 30%',
      trigger: '.animateTrigger',
      // markers: true,
      onEnter: () => {
        setAnimateTrigger(true);
      },
    });
    gsap.fromTo(
      '.award',
      {
        y: '+=120',
        opacity: 0,
      },
      {
        scrollTrigger: {
          scroller: '#app-wrapper',
          trigger: '.prize',
          start: 'top 70%',
          end: 'bottom 30%',
          toggleActions: 'play none none reverse',
        },
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: {
          amount: 1,
          grid: 'auto',
          axis: 'y',
          from: 'start',
        },
      }
    );

    return () => {
      ScrollTrigger.killAll();
    };
  }, []);

  const loopItems = [
    Array(5).fill(['爽膚', '精華', '面霜', '防曬']),
    Array(5).fill(['胭脂', '眼影', '唇妝產品', '眉部產品']),
    Array(5).fill(['底妝遮瑕', '定妝​', '香水', '洗面卸妝']),
  ];

  const [hoverState, setHoverState] = useState([]);

  return (
    <>
      <NextSeo
        title="Sa Sa 美麗「程」獻：2025 Sorra Beauty Awards |  最真實的美妝產品評價 | Sorra"
        description="Sa Sa x Sorra年度票選大賞正式開始！第2屆的主題為「Fly me to the ...」，希望大家在選出陪你渡過2025年旅程的年度好物，打造香港最貼地的美妝排行榜。"
        openGraph={{
          images: [
            {
              url: 'https://www.sorra.net/images/beauty-award/2025/banner_launching.png',
              width: 1200,
              height: 410,
              alt: 'Sorra',
            },
          ],
        }}
      />
      <div
        style={{
          background:
            'linear-gradient(90deg, #FACBD1 1.2%, #FFF5F6 30.36%, #FACBD1 99.86%)',
        }}
        className="relative flex w-full flex-col items-center overflow-x-hidden text-white"
      >
        <p className="absolute left-[18px] top-3.5 hidden text-xs text-sorra-pink lg:block">
          推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
        </p>
        <div
          style={{
            backfaceVisibility: 'hidden',
            transform: `perspective(1px) translateZ(0) scale(${Math.min(desktopScale, 1)})`,
            transformOrigin: originStrategy,
          }}
          // className="relative h-[705px]"
          className="relative hidden w-[1440px] flex-col justify-center overflow-visible lg:flex"
        >
          <div className="flex items-end justify-center gap-x-1.5 pt-24">
            <img
              src="/images/beauty-award/2025/sasa.png"
              className="h-[54px] w-[153px] object-contain"
            />
            <p className="text-[28px] font-normal text-[#414042]">
              美麗「程」獻
            </p>
          </div>
          <div className="relative flex h-[550px] shrink-0 flex-grow flex-row gap-x-10 overflow-visible">
            <div className="absolute bottom-0 left-0 right-0 top-0">
              <SorraCircle className="left-[217px] top-[48px] h-3 w-3" />
              <SorraCircle className="left-[106px] top-[39px] h-4 w-4" />
              <SorraStar className="left-[39px] top-[150px] h-[29px] w-5 rotate-[-12deg]" />
              <SorraCircle className="left-[29px] top-[267px] h-2.5 w-2.5" />
              <SorraCircle className="bottom-[122px] left-[40px] h-2.5 w-2.5" />
              <SorraStar className="bottom-[42px] left-[70px] h-[29px] w-5 rotate-[14.1deg]" />
              <SorraCircle className="right-[594px] top-[33px] h-3 w-3" />
              <SorraStar className="right-[206px] top-[18px] h-[26px] w-[18px] rotate-[15.7deg]" />
              <SorraCircle className="right-[18px] top-[35px] h-3 w-3" />
              <SorraStar className="right-[47px] top-[175px] h-[29px] w-[20px]" />
              <SorraCircle className="right-[37px] top-[385px] h-2.5 w-2.5" />
              <SorraStar className="bottom-[16px] right-[192px] h-[22px] w-[15px]" />
              <SorraCircle className="bottom-[16px] left-[472px] h-2.5 w-2.5" />
            </div>

            <div
              ref={desktopFrameRef}
              style={{
                boxShadow: '-8px 8px 0 0 #F8A5B1',
              }}
              className="absolute left-[71px] right-[71px] top-[47px] aspect-[1282/438] rounded-full border-[2px] border-[#4F4E4F] bg-[#FFFCFB]"
            >
              <div className="absolute left-[175px] right-[175px] top-[45px] flex flex-col items-center">
                <img
                  src="/images/beauty-award/2025/2025_sorra_beauty_award_result.png"
                  className="mt-6 h-[158px] w-[784px]"
                />
                {/* <img
                  src="/images/beauty-award/2025/desktop_fly_me_to_the.png"
                  className="mt-6 h-[48px] w-[732px] object-contain"
                /> */}
                <div className="mt-8 flex w-[784px] flex-row items-end gap-x-2 text-black">
                  <div className="relative">
                    <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                    <p className="relative font-rubik text-[32px] font-normal text-[#4F4E4F]">
                      Fly me to the
                    </p>
                  </div>
                  <div className="h-[2px] w-[19px]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="2"
                      viewBox="0 0 19 2"
                      fill="none"
                    >
                      <path
                        d="M0.769997 1.58334C1.19526 1.58334 1.54 1.2289 1.54 0.791671C1.54 0.354443 1.19526 0 0.769997 0C0.344737 0 0 0.354443 0 0.791671C0 1.2289 0.344737 1.58334 0.769997 1.58334Z"
                        fill="#4F4E4F"
                      />
                      <path
                        d="M9.65988 1.58334C10.0851 1.58334 10.4299 1.2289 10.4299 0.791671C10.4299 0.354443 10.0851 0 9.65988 0C9.23462 0 8.88989 0.354443 8.88989 0.791671C8.88989 1.2289 9.23462 1.58334 9.65988 1.58334Z"
                        fill="#4F4E4F"
                      />
                      <path
                        d="M17.7099 1.58334C18.1352 1.58334 18.4799 1.2289 18.4799 0.791671C18.4799 0.354443 18.1352 0 17.7099 0C17.2847 0 16.9399 0.354443 16.9399 0.791671C16.9399 1.2289 17.2847 1.58334 17.7099 1.58334Z"
                        fill="#4F4E4F"
                      />
                    </svg>
                  </div>
                  <div className="h-px grow bg-[#4F4E4F]"></div>
                </div>
                <div className="mt-9 flex w-[784px] flex-row gap-x-[90px] text-[#4F4E4F]">
                  <div className="flex flex-col items-start">
                    <div className="relative">
                      <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[40%] bg-[#FFE5E9]"></div>
                      <p className="relative font-rubik text-lg font-normal text-[#4F4E4F]">
                        Flight
                      </p>
                    </div>
                    <p className="text-[24px] leading-[150%]">SR 2025</p>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="relative">
                      <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[40%] bg-[#FFE5E9]"></div>
                      <p className="relative font-rubik text-lg font-normal text-[#4F4E4F]">
                        Voting Period
                      </p>
                    </div>
                    <p className="text-[24px] leading-[150%]">22/12 - 11/01</p>
                  </div>
                </div>
                {/* <img
                  src="/images/beauty-award/2025/desktop_flight_period.png"
                  className="mt-9 h-[63px] w-[326px] object-contain"
                /> */}
              </div>
              <div
                className={classnames(
                  'absolute bottom-[67px] left-[974px] right-[89px] flex h-[317px] flex-col items-center overflow-visible rounded-3xl py-[46px]'
                )}
              >
                <img
                  src="/images/beauty-award/2025/plate.png"
                  className="absolute bottom-5 right-0 h-[135px] w-[103px] translate-x-1/2 object-contain"
                />

                <div className="absolute bottom-0 right-0 translate-x-[10%] translate-y-1/2 rotate-[-4.496deg] border border-[#474748] bg-[#FFFDFE] px-2.5 py-1.5">
                  <p className="whitespace-nowrap text-[26px] font-bold tracking-[1.3px] text-[#4A4A4A]">
                    選出陪你渡過2025旅程的
                    <span className="text-sorra-pink">年度美妝好物</span>!
                  </p>
                </div>
              </div>
            </div>
            <img
              ref={ballonRef}
              src="/images/beauty-award/2025/ballon.png"
              className="absolute left-[100px] top-[50px] h-[135px] w-[135px] rotate-[40deg] object-contain"
            />
            <img
              ref={redBowRef}
              src="/images/beauty-award/2025/pink_bow.png"
              className="absolute bottom-[240px] left-10 h-[85px] w-[80px] rotate-[5.61deg] object-contain"
            />
            <img
              ref={blueBowRef}
              src="/images/beauty-award/2025/blue_bow.png"
              className="absolute right-[16px] top-[220px] h-[135px] w-[135px] -rotate-[8.5deg] object-contain"
            />
            <img
              ref={lipRef}
              src="/images/beauty-award/2025/lip.png"
              className="absolute bottom-5 left-[132px] h-[171px] w-[65px] -rotate-[3.04deg] object-contain"
            />
            <img
              ref={planeRef}
              src="/images/beauty-award/2025/plane.png"
              className="absolute right-[54px] top-[12px] h-[154px] w-[231px] -rotate-[0.88deg] object-contain"
            />
          </div>
          {/* <div className="h-[98px]" /> */}
        </div>
        <div
          className="absolute right-15 top-10 hidden cursor-pointer items-center gap-x-3 rounded-full bg-sorra-pink px-6 py-3 lg:flex"
          onClick={() => {
            router.push('/');
          }}
        >
          <p className="text-white">前往SORRA主頁 </p>
          <div className="h-3 w-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M0.292893 12.2929C-0.0976311 12.6834 -0.0976311 13.3166 0.292893 13.7071C0.683418 14.0976 1.31658 14.0976 1.70711 13.7071L0.292893 12.2929ZM14 0.999999C14 0.447714 13.5523 -8.61581e-07 13 -1.11446e-06L4 -3.13672e-07C3.44772 -6.50847e-07 3 0.447715 3 0.999999C3 1.55228 3.44772 2 4 2L12 2L12 10C12 10.5523 12.4477 11 13 11C13.5523 11 14 10.5523 14 10L14 0.999999ZM1.70711 13.7071L13.7071 1.70711L12.2929 0.292893L0.292893 12.2929L1.70711 13.7071Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className="relative block w-full overflow-x-hidden bg-[#FFCCD4] text-white lg:hidden">
          <div className="bg-white">
            <div
              className="relative flex flex-col"
              style={{
                background:
                  'linear-gradient(206deg, #FFE5E9 35.86%, rgba(235, 89, 148, 0.50) 73.84%)',
              }}
            >
              <div className="flex items-end justify-center gap-x-1.5 pb-11 pt-7">
                <img
                  src="/images/beauty-award/2025/sasa.png"
                  className="h-[39px] w-[109px] object-contain"
                />
                <br />
                <p className="text-sm font-normal text-[#414042]">
                  美麗「程」獻
                </p>
              </div>
              <div className="relative flex grow flex-col px-4.5">
                <div className="grow rounded-t-[120px] border-x-[2px] border-t-[2px] border-[#4F4E4F] bg-[#FFFCFB] px-8 pb-[43px] pt-10">
                  <img
                    src="/images/beauty-award/2025/2025.png"
                    className="absolute left-1/2 top-0 h-[41px] w-[115px] -translate-x-1/2 -translate-y-1/2"
                  />
                  <div className="flex justify-center">
                    <img
                      className="h-[95px] w-[238px]"
                      src="/images/beauty-award/2025/mobile_sorra_beauty.png"
                    />
                  </div>

                  <div className="mt-8 flex w-full flex-row items-end gap-x-2 text-black">
                    <div className="relative text-base text-black">
                      <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                      <p className="relative font-rubik">Fly me to the</p>
                    </div>
                    <div className="mb-1 h-[2px] w-[19px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="2"
                        viewBox="0 0 19 2"
                        fill="none"
                      >
                        <path
                          d="M0.769997 1.58334C1.19526 1.58334 1.54 1.2289 1.54 0.791671C1.54 0.354443 1.19526 0 0.769997 0C0.344737 0 0 0.354443 0 0.791671C0 1.2289 0.344737 1.58334 0.769997 1.58334Z"
                          fill="#4F4E4F"
                        />
                        <path
                          d="M9.65988 1.58334C10.0851 1.58334 10.4299 1.2289 10.4299 0.791671C10.4299 0.354443 10.0851 0 9.65988 0C9.23462 0 8.88989 0.354443 8.88989 0.791671C8.88989 1.2289 9.23462 1.58334 9.65988 1.58334Z"
                          fill="#4F4E4F"
                        />
                        <path
                          d="M17.7099 1.58334C18.1352 1.58334 18.4799 1.2289 18.4799 0.791671C18.4799 0.354443 18.1352 0 17.7099 0C17.2847 0 16.9399 0.354443 16.9399 0.791671C16.9399 1.2289 17.2847 1.58334 17.7099 1.58334Z"
                          fill="#4F4E4F"
                        />
                      </svg>
                    </div>
                    <div className="mb-[5px] h-px grow bg-[#4F4E4F]"> </div>
                  </div>
                  <div className="mt-3 flex w-full flex-row items-end gap-x-11 text-[#4F4E4F]">
                    <div className="flex flex-col items-start">
                      <div className="relative text-xs">
                        <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                        <p className="relative">Flight</p>
                      </div>
                      <p className="text-sm font-light">SR 2025</p>
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="relative text-xs">
                        <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                        <p className="relative">Voting Period</p>
                      </div>
                      <p className="text-sm font-light">22/12 - 11/01</p>
                    </div>
                  </div>
                  <div className="mt-6 translate-x-[5%] rotate-[-2.8deg] text-[20px] font-medium tracking-[0.57px] text-[#414042]">
                    <div
                      style={{
                        background:
                          'linear-gradient(90deg, #FFF 0%, #FDF7F3 99.98%)',
                      }}
                      className="-translate-x-[35%] border border-[#414042] px-4 py-1"
                    >
                      <p className="text-right">選出陪你渡過2025旅程</p>
                    </div>
                    <div
                      style={{
                        background:
                          'linear-gradient(90deg, #FFF 0%, #FDF7F3 99.98%)',
                      }}
                      className="-translate-y-[1px] translate-x-[45%] border border-[#414042] px-4 py-1"
                    >
                      <p className="text-left">
                        的<span className="text-sorra-pink">年度美妝好物</span>!
                      </p>
                    </div>
                  </div>
                </div>
                <img
                  ref={mobilePlaneRef}
                  src="/images/beauty-award/2025/plane.png"
                  className="absolute left-4 top-0 h-[79px] w-[119px] rotate-[-3.019deg] object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="relative flex w-full flex-col px-5 pb-[65px] pt-15 text-center font-jinxuanlatte text-base text-black lg:w-auto lg:px-6 lg:text-[24px]">
          <div
            className="absolute left-0 top-0 h-full w-full bg-white"
            style={{ filter: 'blur(50px)' }}
          ></div>
          <p className="relative">
            2025年Sorra年度票選大賞經已圓滿
            <br className="block lg:hidden" />
            結束，本年度SORRA一共收到了⋯
          </p>
          <div className="relative flex flex-row justify-center gap-x-[46px] pt-[14px] text-sm font-normal text-[#4F4E4F] lg:text-[24px] lg:font-medium">
            <div className="hidden flex-col items-center lg:flex">
              <div className="relative mx-1.5 flex text-sorra-pink">
                <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                <SpinningNumber width={40} number={28613} fontSize={64} />
              </div>
              <p>篇產品評價</p>
            </div>
            <div className="hidden flex-col items-center lg:flex">
              <div className="relative mx-1.5 flex text-sorra-pink">
                <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                <SpinningNumber width={40} number={37106} fontSize={64} />
              </div>
              <p>經認證票數</p>
            </div>
            <div className="flex flex-col items-center lg:hidden">
              <div className="relative mx-1.5 flex text-sorra-pink">
                <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                <SpinningNumber width={25} number={28613} fontSize={36} />
              </div>
              <p>篇產品評價</p>
            </div>
            <div className="flex flex-col items-center lg:hidden">
              <div className="relative mx-1.5 flex text-sorra-pink">
                <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                <SpinningNumber width={25} number={37106} fontSize={36} />
              </div>
              <p>經認證票數</p>
            </div>
          </div>
          <p className="relative pt-10">
            第2屆的主題為「選出陪你渡過2025
            <br className="block lg:hidden" />
            旅程的年度美妝好物!」，
            <br />
            希望大家選擇最能有效改善皮膚的產
            <br className="block lg:hidden" />
            品及最有好感的廣告代言組合～
          </p>
        </div>
        <div className="flex flex-col items-center pt-[39px] text-black lg:pt-[69px]">
          <p className="font-jinxuanlatte text-[32px] font-extrabold lg:text-[48px]">
            分類排名榜
          </p>
          <div className="aspect-[264/32] w-[140px] lg:w-[264px]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 264 32"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M193.635 13.1553C204.977 10.2635 218.072 7.38564 230.67 6.66704C241.437 6.05371 251.837 7.00634 260.35 11.0595C261.55 11.6302 263.053 11.2379 263.702 10.1818C264.352 9.12649 263.904 7.80496 262.704 7.23339C253.418 2.81306 242.095 1.65685 230.35 2.32673C217.526 3.0575 204.195 5.95456 192.625 8.88811C191.283 6.49656 188.503 4.26681 183.606 2.62169C176.084 0.0944195 165.815 0.125742 154.996 1.54989C139.305 3.61434 122.426 8.5375 111.573 12.0357C111.128 12.1783 110.465 12.4054 109.654 12.7047C109.407 12.2244 109.13 11.7512 108.823 11.2866C106.162 7.23774 101.453 3.96404 96.6551 2.79479C66.2249 -4.61912 30.7099 4.2642 1.79327 11.4937C0.487427 11.8217 -0.274292 13.0257 0.0917664 14.1801C0.467682 15.3346 1.83286 16.0053 3.14859 15.6773C31.254 8.6497 65.76 -0.217026 95.3394 6.98722C98.98 7.8746 102.522 10.3905 104.54 13.4633C104.767 13.806 104.965 14.1566 105.153 14.5125C101.166 16.2498 96.4771 18.6718 93.9544 21.2643C91.3625 23.9221 90.7195 26.7591 92.975 29.4386C94.914 31.7292 97.8522 31.9241 100.909 30.7366C104.203 29.4647 107.597 26.4912 108.695 25.0957C110.752 22.4893 111.405 19.6618 111.029 16.9049C111.999 16.5447 112.786 16.2768 113.272 16.1202C123.887 12.6995 140.385 7.87107 155.728 5.85186C165.6 4.55212 174.977 4.37902 181.841 6.68445C184.768 7.66839 186.702 8.81673 187.752 10.1495C185.68 10.6941 183.683 11.2301 181.775 11.7416C178 12.7534 170.143 14.4281 164.528 17.4129C160.064 19.7862 157.037 23.0147 157.213 27.0618C157.303 29.1411 158.671 30.5287 160.848 31.3064C164.102 32.4679 169.729 31.9059 171.824 31.5144C178.18 30.3295 186.662 25.4245 190.858 19.8976C192.533 17.6922 193.529 15.385 193.635 13.1553ZM188.415 14.5142C186.619 14.9892 184.884 15.4555 183.217 15.9027C179.694 16.8475 172.33 18.3482 167.088 21.1356C164.312 22.6111 162.045 24.3788 162.155 26.8957C162.164 27.0914 162.385 27.1471 162.58 27.2271C162.881 27.3498 163.227 27.4342 163.595 27.5003C166.085 27.9475 169.429 27.5151 170.799 27.2593C176.131 26.265 183.224 22.1195 186.745 17.4817C187.489 16.5021 188.081 15.5042 188.415 14.5142ZM106.142 18.9076C103.818 19.9594 101.384 21.2313 99.4647 22.6154C98.4457 23.3505 97.5851 24.1065 97.041 24.8912C96.5859 25.5402 96.4079 26.2049 96.952 26.847C97.1696 27.1071 97.496 27.1123 97.8324 27.0636C98.1885 27.0114 98.5546 26.9 98.9206 26.7565C101.334 25.8221 103.827 23.6394 104.629 22.6154C105.569 21.4279 106.034 20.1716 106.142 18.9076Z"
                fill="#F16B7C"
              />
            </svg>
          </div>
        </div>
        <MaxWidthContainer
          noPending
          className="relative mt-6 flex h-[134px] overflow-visible lg:mt-12 lg:h-[268px]"
        >
          <div className="absolute left-1/2 top-0 h-full w-[400px] -translate-x-1/2 overflow-visible lg:w-[1440px]">
            {Array(resultKeys.length)
              .fill(0)
              .map((_, i) => {
                return (
                  <div
                    key={i}
                    className="absolute left-1/2 top-0 aspect-[145/135] w-1/3 -translate-x-1/2 lg:w-1/5"
                  >
                    <div
                      style={{
                        transformOrigin: 'top',
                        transform: ' translateX(300%) scale(0.7)',
                      }}
                      className={`feature-${i} flex h-full w-full items-center justify-center opacity-0`}
                    >
                      <RollingItem i={i} />
                    </div>
                  </div>
                );
              })}
          </div>
        </MaxWidthContainer>
        {/* <MaxWidthContainer
          noPending
          className="relative mt-6 flex h-[160px] justify-center overflow-visible bg-green-300 lg:mt-12 lg:h-[268px]"
        >
          <div className="relative h-full w-full overflow-visible">
            {Array(resultKeys.length)
              .fill(0)
              .map((_, i) => {
                return (
                  <div
                    key={i}
                    className="absolute left-1/2 top-0 aspect-[145/135] w-1/3 -translate-x-1/2 lg:w-1/5"
                  >
                    <div
                      style={{
                        transformOrigin: 'top',
                        transform: ' translateX(300%) scale(0.7)',
                      }}
                      className={`feature-${i} flex h-full w-full items-center justify-center bg-red-500 opacity-0`}
                    >
                      <RollingItem i={i} />
                    </div>
                  </div>
                );
              })}
          </div>
        </MaxWidthContainer> */}
        <div className="mt-9 justify-center lg:mt-11 lg:flex">
          <button
            className="w-[268px] rounded-full bg-sorra-pink px-10 py-3 text-base font-semibold text-white lg:w-auto lg:text-2xl"
            onClick={() => {
              router.push('/beautyawards2025/result/ranking');
            }}
          >
            查看完整排名
          </button>
        </div>
        <div className="pt-[78px]">
          <img
            src="/images/beauty-award/2025/2025_sorra_beauty_award_research.png"
            className="aspect-[651/230] w-[283px] object-contain lg:w-[651px]"
          />
        </div>
        <div className="w-full px-4 pt-10 text-black lg:w-auto lg:px-0 lg:pt-[98px]">
          <div className="flex flex-col justify-normal gap-x-12 gap-y-10 lg:mt-[51px] lg:flex-row lg:justify-center">
            <div
              style={{
                boxShadow: '3px 3px 4.5px 0px rgba(0, 0, 0, 0.05)',
              }}
              className="relative flex h-auto w-full flex-col items-center gap-x-6 gap-y-4 rounded-xl bg-white px-6 pb-4 pt-7 lg:h-[312px] lg:w-[578px] lg:flex-row lg:pb-0 lg:pt-0"
            >
              <CardTitle n={1} title="Sorra會員的平均年齡為…" />
              <img
                src="/images/beauty-award/2025/2025_sorra_beauty_award_circle_chart.png"
                className="aspect-square w-[145px] lg:w-[197px]"
              />
              <p className="text-center text-sm font-normal leading-[150%] lg:text-left lg:text-[24px]">
                Sorra讀者以90後佔最多，其次就是Gen-Z！而36歲或以上則佔21%，證明愛美是不分年齡的～
              </p>
            </div>
            <div
              style={{
                boxShadow: '3px 3px 4.5px 0px rgba(0, 0, 0, 0.05)',
              }}
              className="relative flex h-auto w-full flex-col items-center justify-center gap-x-6 gap-y-1.5 rounded-xl bg-white px-6 pb-4 pt-7 lg:h-[312px] lg:w-[578px] lg:pb-0 lg:pt-0"
            >
              <CardTitle n={2} title="每月用於美妝產品的預算…" />

              <img
                src="/images/beauty-award/2025/2025_sorra_beauty_award_statistic.png"
                className="hidden h-[163px] w-[491px] lg:block"
              />
              <div className="block px-2 lg:hidden">
                <img
                  src="/images/beauty-award/2025/2025_sorra_beauty_award_statistic_mobile.png"
                  className="aspect-[280/122] w-full"
                />
              </div>
              <p className="text-center text-sm lg:text-2xl">
                Sorra讀者大家都是精打細算的小資女，
                <br />
                接近4成讀者每月消費$500-1,000 於美妝產品上
              </p>
              <div className="absolute right-0 top-1/2 aspect-[82/96] w-[67px] -translate-y-1/2 translate-x-[15%] rotate-[-13.132deg] lg:w-[82px]">
                <img
                  src="/images/beauty-award/2025/rabbit_scope.png"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col justify-normal gap-x-12 gap-y-10 lg:mt-14 lg:flex-row lg:justify-center">
            <div
              style={{
                boxShadow: '3px 3px 4.5px 0px rgba(0, 0, 0, 0.05)',
              }}
              className="relative flex h-auto w-full items-center justify-center gap-y-2 rounded-xl bg-white px-6 py-6 lg:h-[312px] lg:w-[578px] lg:py-0"
            >
              <CardTitle n={3} title="會員最常留意的平台…" />
              {Array(3)
                .fill(0)
                .map((_, i) => {
                  return (
                    <div
                      key={i}
                      className="flex flex-1 flex-shrink-0 flex-col items-center justify-center"
                    >
                      <img
                        style={{
                          height: '50px',
                          width: i == 0 ? '58px' : '40px',
                        }}
                        className="object-contain object-bottom"
                        src={`/images/2024/result/mobile/crown_${i}.png`}
                      />
                      <div className="mt-3 flex h-[68px] w-auto items-center justify-center lg:mt-[22px] lg:h-[96px] lg:w-[96px]">
                        <img
                          className="hidden aspect-square object-contain lg:block"
                          style={{
                            width: i == 0 ? '96px' : '80px',
                          }}
                          src={`/images/beauty-award/2025/${i == 0 ? 'ig' : i == 1 ? 'thread' : 'yt'}.png`}
                        />
                        <img
                          className="block aspect-square object-contain object-bottom lg:hidden"
                          style={{
                            width: i == 0 ? '68px' : '60px',
                          }}
                          src={`/images/beauty-award/2025/${i == 0 ? 'ig' : i == 1 ? 'thread' : 'yt'}.png`}
                        />
                      </div>
                      <p className="mt-3 font-rubik text-base font-normal text-sorra-pink lg:mt-[15px] lg:text-[28px] lg:font-semibold">
                        {i == 0 ? 'Instagram' : i == 1 ? 'Threads' : 'YouTube'}
                      </p>
                    </div>
                  );
                })}
            </div>
            <div
              style={{
                boxShadow: '3px 3px 4.5px 0px rgba(0, 0, 0, 0.05)',
              }}
              // className="relative flex h-[312px] w-[578px] items-center justify-center gap-x-4 rounded-xl bg-white px-6"
              className="relative flex h-auto w-full items-center justify-center gap-y-2 rounded-xl bg-white px-6 py-6 lg:h-[312px] lg:w-[578px] lg:py-0"
            >
              <CardTitle n={4} title="所有分類的總冠軍產品是…" />
              {/* <div className="flex w-full justify-center"> */}
              <img
                className="block aspect-[141/237] w-[82px] object-contain lg:w-[141px]"
                src="/images/beauty-award/2025/bioderma.png"
              />
              <div className="flex flex-col text-[#111928]">
                <p className="text-sm text-[#4A4A4A] lg:text-[20px]">
                  Bioderma
                </p>
                <p className="mt-2 text-[15px] font-medium lg:mt-0 lg:text-2xl">
                  防敏護理系列-深層卸妝潔膚水
                </p>
                <p className="mt-3 text-left font-rubik text-sm leading-[150%] lg:mt-2.5 lg:text-2xl">
                  會員讚賞其清潔力及保濕效果，即使對敏感肌也友好，使用後不會感到緊繃或刺激。
                </p>
                <div
                  className="mt-3 flex cursor-pointer items-center gap-x-2 lg:mt-4"
                  onClick={() => {
                    router.push('/product/275');
                  }}
                >
                  <p className="text-base font-medium text-sorra-pink lg:text-2xl">
                    查看產品資料及更多評價
                  </p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.352 3.95095C12.577 3.72599 12.8822 3.59961 13.2004 3.59961C13.5186 3.59961 13.8238 3.72599 14.0488 3.95095L21.2488 11.151C21.4738 11.376 21.6001 11.6812 21.6001 11.9994C21.6001 12.3176 21.4738 12.6227 21.2488 12.8478L14.0488 20.0478C13.8225 20.2663 13.5193 20.3873 13.2047 20.3846C12.8901 20.3818 12.5891 20.2556 12.3666 20.0331C12.1441 19.8106 12.0179 19.5097 12.0152 19.195C12.0124 18.8804 12.1334 18.5773 12.352 18.351L17.5036 13.1994H3.60039C3.28213 13.1994 2.97691 13.0729 2.75186 12.8479C2.52682 12.6228 2.40039 12.3176 2.40039 11.9994C2.40039 11.6811 2.52682 11.3759 2.75186 11.1508C2.97691 10.9258 3.28213 10.7994 3.60039 10.7994H17.5036L12.352 5.64775C12.127 5.42272 12.0006 5.11755 12.0006 4.79935C12.0006 4.48116 12.127 4.17599 12.352 3.95095Z"
                      fill="#F16B7C"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col overflow-y-visible lg:mt-14 lg:flex-row lg:justify-center">
            <div
              style={{
                boxShadow: '3px 3px 4.5px 0px rgba(0, 0, 0, 0.05)',
              }}
              // className="relative mx-5 flex h-auto w-auto items-center gap-x-[14px] rounded-xl bg-white px-5 pb-4 pt-7 lg:h-[351px] lg:w-[632px]"
              className="relative mx-0 flex h-auto w-full flex-col items-center justify-center gap-x-20 gap-y-6 rounded-xl bg-white px-0 pb-8 pt-7 lg:mx-5 lg:h-[312px] lg:w-[1204px] lg:flex-row lg:pb-0 lg:pt-0"
            >
              <CardTitle n={5} title="最有好感的美妝代言組合是…" />
              <div className="flex w-full flex-col lg:w-[495px] lg:px-0">
                <p className="text-center font-jinxuanlatte text-[56px] font-extrabold text-sorra-pink opacity-20 lg:text-[60px]">
                  香港代言組
                </p>
                <div className="no-scrollbar -mt-7 flex w-full flex-row justify-start gap-x-3 overflow-x-auto px-2 lg:justify-center lg:gap-x-6 lg:px-0">
                  {Array(3)
                    .fill(0)
                    .map((_, i) => {
                      return (
                        <div className="flex flex-1 flex-col items-center gap-y-2">
                          <img
                            style={{
                              height: '51px',
                              width: '43px',
                            }}
                            className="object-contain object-bottom"
                            src={`/images/2024/result/mobile/crown_${i}.png`}
                          />
                          <div className="aspect-[149/112] w-[148px] overflow-hidden rounded-lg lg:w-full">
                            <img
                              src={`/images/beauty-award/2025/agents/hk/rank/${i}.png`}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <p className="whitespace-pre-line text-center text-sm font-bold text-[#4F4E4F]">
                            {i == 0
                              ? '魏浚笙 (Jeffrey Ngai)\r\nDIOR Beauty'
                              : i == 1
                                ? '鄧麗欣 (Stephy Tang)\r\nSHIMBI METHOD'
                                : '林家謙 (Terence Lam)\r\nLA MER'}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className="flex w-full flex-col lg:w-[495px] lg:px-0">
                <p className="text-center font-jinxuanlatte text-[56px] font-extrabold text-sorra-pink opacity-20 lg:text-[60px]">
                  日韓代言組
                </p>
                <div className="no-scrollbar -mt-7 flex w-full flex-row justify-start gap-x-3 overflow-x-auto px-2 lg:justify-center lg:gap-x-6 lg:px-0">
                  {Array(3)
                    .fill(0)
                    .map((_, i) => {
                      return (
                        <div className="flex flex-1 flex-col items-center gap-y-2">
                          <img
                            style={{
                              height: '51px',
                              width: '43px',
                            }}
                            className="object-contain object-bottom"
                            src={`/images/2024/result/mobile/crown_${i}.png`}
                          />
                          <div className="aspect-[149/112] w-[148px] overflow-hidden rounded-lg lg:w-full">
                            <img
                              src={`/images/beauty-award/2025/agents/jk/rank/${i}.png`}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <p className="whitespace-pre-line text-center text-sm font-bold text-[#4F4E4F]">
                            {i == 0
                              ? 'IU\r\nESTÉE LAUDER'
                              : i == 1
                                ? '張員瑛 (IVE)\r\nAMUSE'
                                : '車銀優\r\nAbib'}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center pb-[84px] pt-[56px]">
            <div
              className="flex w-[268px] cursor-pointer items-center justify-center gap-x-3 rounded-full bg-sorra-pink px-6 py-3 lg:w-auto"
              onClick={() => {
                router.push('/');
              }}
            >
              <p className="text-white">前往SORRA主頁 </p>
              <div className="h-3 w-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M0.292893 12.2929C-0.0976311 12.6834 -0.0976311 13.3166 0.292893 13.7071C0.683418 14.0976 1.31658 14.0976 1.70711 13.7071L0.292893 12.2929ZM14 0.999999C14 0.447714 13.5523 -8.61581e-07 13 -1.11446e-06L4 -3.13672e-07C3.44772 -6.50847e-07 3 0.447715 3 0.999999C3 1.55228 3.44772 2 4 2L12 2L12 10C12 10.5523 12.4477 11 13 11C13.5523 11 14 10.5523 14 10L14 0.999999ZM1.70711 13.7071L13.7071 1.70711L12.2929 0.292893L0.292893 12.2929L1.70711 13.7071Z"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BeautyAwards2025Result;
